import { formatDate, formatDateTime } from '@/lib/date-time';
/**
 * @file routes/_app/dispatch/$caseId.tsx
 * @description 案件詳情頁面（M1）。
 *
 * 顯示案件完整資料：
 * - Header Card：案件基本資料（編號、客戶、狀態、到期日）
 * - 進度步驟條（CaseProgressSteps）：兼任內容切換導航。點擊步驟即切換下方面板，
 *   取代原本獨立的 Tabs，避免「步驟條 + Tabs」兩層重複導航：
 *   0. 文件審核 → 文件       — 申請文件審核 + 案件附件列表 + 上傳
 *   1. 合約評審 → 合約評審   — 合約評審面板（評審歷史 / 受理 / 駁回）
 *   2. 報價     → 報價       — 報價單列表 + 新增
 *   3. 繳費     → 繳費       — 費用記錄列表 + 記錄繳款 + 確認繳費
 *   4. 派遣     → 派遣團隊   — 稽核成員列表 + 首次/改派操作
 *   5. 已派案   → 狀態日誌   — 不可變審計流水日誌（僅顯示）
 *
 * 視覺：PageShell + SectionCard + StatusPill + DataField
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Form,
  Input,
  InputNumber,
  List,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Timeline,
  Typography,
  Upload,
} from 'antd';
import { message, modal } from '@/lib/antd-app';
import {
  ArrowLeft,
  CheckCircle2,
  CalendarPlus,
  Copy,
  Pencil,
  Plus,
  RefreshCw,
  X,
  Upload as UploadIcon,
} from 'lucide-react';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { apiClient } from '@/lib/api-client';
import { useAuthStore } from '@/lib/auth-context';
import { CASE_DOC_CATEGORIES, type CaseDocCategory } from '@/lib/case-doc-categories';
import { isPreDispatchCase, userCan } from '@zhcb/shared';
import {
  expandZipFiles,
  runWithConcurrency,
  uploadFile,
  uploadFiles,
  type UploadedFile,
} from '@/lib/file-upload';
import { PageShell, StatusPill, type StatusPillProps } from '@/components/shell';
import { SignaturePadModal } from '@/components/common/SignaturePadModal';
import { FileLink } from '@/components/common/FileLink';
import { CaseDocumentReviewPanel } from '@/components/dispatch/CaseDocumentReviewPanel';
import { CaseDocCategoryRow } from '@/components/dispatch/CaseDocCategoryRow';
import {
  canFinalizeQuotation,
  isDispatchManager,
} from '@/components/dispatch/case-detail-role-guards';
import { ContractReviewPanel } from '@/components/contract-review/ContractReviewPanel';
import { CaseProgressSteps, getDefaultActiveStep } from '@/components/dispatch/CaseProgressSteps';
import { ApplicationDetailsView } from '@/components/dispatch/ApplicationDetailsView';
import { CaseEditDrawer } from '@/components/dispatch/CaseEditDrawer';
import { TaskPackButton } from '@/components/audit-tasks/TaskPackButton';

/** TanStack Router 路由定義 */
export const Route = createFileRoute('/_app/dispatch/$caseId')({ component: CaseDetailPage });

// ─── 類型定義 ─────────────────────────────────────────────────────────────────

interface CaseDetail {
  id: number;
  caseCode: string;
  clientId: number;
  clientName: string | null;
  clientCode: string | null;
  scheme: string;
  contractSource: string | null;
  verificationType: string;
  verificationCategories: unknown;
  /** 驗證資格（多選陣列；後端已回落舊單值資料，永遠是陣列，可能為空） */
  verificationQualifications: string[];
  /** 申請明細 jsonb 原始值（TOP/TAP 新結構或 LEGACY 舊結構；前端 parse 兼容渲染） */
  applicationDetails: unknown;
  workflowState: string;
  /** 合約評審子狀態（受理建檔期間用於閘門） */
  contractReviewState:
    | 'pending'
    | 'in_review'
    | 'accepted'
    | 'rejected_fixable'
    | 'rejected_terminal';
  assignedTeamLeadId: number | null;
  teamLeadName: string | null;
  dispatchedAt: string | null;
  /**
   * 預派案時間（2026-09-11）：非 null 代表本案走「預派案」——先派案，
   * 應附文件 / 合約評審 / 報價 / 繳費四步改為派案後補辦。
   */
  preDispatchedAt: string | null;
  dueDate: string | null;
  /** 稽核完成期限（客戶 PPT S47；管理組派案時指定「XXXX年XX月XX日前」，未指定為 null） */
  auditDeadline: string | null;
  closedAt: string | null;
  notes: string | null;
  /** 管理組備註（派案 / 改派時填寫，客戶 PPT S23；未填為 null） */
  adminNotes: string | null;
  /** 驗證組備註（派案 / 改派時填寫，客戶 PPT S23；未填為 null） */
  verificationNotes: string | null;
  /** 客戶最近一張證書的效期（序105：派遣頁日期自動導入來源；無證書為 null） */
  clientLatestCert: { issuedAt: string | null; expiresAt: string | null } | null;
  /** 序110：申請表線上填寫 token（null＝未生成連結） */
  applicationFormToken: string | null;
  /** 序110：客戶線上提交的申請表內容（null＝未填寫） */
  onlineApplication: { submittedAt: string; data: Record<string, unknown> } | null;
  version: number;
  createdAt: string;
  updatedAt: string;
  documents: CaseDocument[];
  /** 最新文件審核狀態（null 表示尚未提交審核） */
  latestDocumentReview: {
    status: 'pending' | 'approved' | 'rejected';
  } | null;
  team: TeamMember[];
  quotations: Quotation[];
  payments: Payment[];
  stateLog: StateLogEntry[];
}

interface CaseDocument {
  id: number;
  docType: string;
  fileId: number;
  /** 沿用來源案件；null 表示本案直接上傳。 */
  sourceCaseId: number | null;
  /** 沿用來源文件關聯 ID。 */
  sourceDocumentId: number | null;
  note: string | null;
  createdAt: string;
  /** LEFT JOIN files 帶出的原始檔名（理論上文件存在時必有；軟刪除後為 null） */
  filename: string | null;
  /** LEFT JOIN files 帶出的檔案大小（位元組） */
  sizeBytes: number | null;
  /** LEFT JOIN files 帶出的 MIME 類型 */
  mimeType: string | null;
}

interface TeamMember {
  id: number;
  userId: number;
  userName: string | null;
  userUsername: string | null;
  role: string;
  confirmedAt: string | null;
  /** 確認時手寫簽名圖（PNG）對應的 files 表 ID；未確認時為 null */
  signatureFileId: number | null;
  addedAt: string;
}

interface Quotation {
  id: number;
  quotationCode: string;
  amount: number;
  breakdown: unknown;
  /** 報價單 PDF 文件 ID（已上傳則有值） */
  fileId: number | null;
  notes: string | null;
  /** 繳費截止日期（序103） */
  paymentDueAt: string | null;
  issuedAt: string | null;
  confirmedAt: string | null;
  createdAt: string;
  /** LEFT JOIN files 帶出的原始檔名（fileId 為 null 時自然為 null） */
  attachmentFilename: string | null;
  attachmentSizeBytes: number | null;
  attachmentMimeType: string | null;
}

interface Payment {
  id: number;
  paymentCode: string;
  amount: number;
  paymentType: string;
  paymentMethod: string | null;
  /** 繳費憑證文件 ID（已上傳則有值） */
  proofFileId: number | null;
  paidAt: string | null;
  confirmedAt: string | null;
  notes: string | null;
  createdAt: string;
  /** LEFT JOIN files 帶出的原始檔名 */
  proofFilename: string | null;
  proofSizeBytes: number | null;
  proofMimeType: string | null;
}

interface StateLogEntry {
  id: number;
  fromState: string | null;
  toState: string;
  reason: string | null;
  actorId: number | null;
  /** 操作者姓名（後端 LEFT JOIN users.name 帶出） */
  actorName: string | null;
  /** 操作者帳號（後端 LEFT JOIN users.username 帶出） */
  actorUsername: string | null;
  createdAt: string;
}

interface AuditorUser {
  id: number;
  username: string;
  name: string;
  /** 多角色：員工列表 API 回傳的角色能力清單 */
  roles: string[];
}

// ─── 狀態顏色 ─────────────────────────────────────────────────────────────────

const STATE_TONE: Record<string, StatusPillProps['tone']> = {
  受理建檔: 'info',
  合約已簽: 'brand',
  已報價: 'brand',
  已收費: 'warning',
  已派案: 'success',
  已繳費: 'success', // 兼容旧数据
  稽核中: 'warning',
  報告中: 'brand',
  報告評審: 'warning',
  決定中: 'info',
  已發證: 'success',
  已寄送: 'success',
  結案: 'muted',
  NC整改中: 'danger',
};

// ─── 子組件 ───────────────────────────────────────────────────────────────────

/**
 * 文件 Tab。
 *
 * 與「新建案件精靈」步驟 3 保持一致：依 SOP 申請材料分類逐項上傳，
 * 每個分類一行（CaseDocCategoryRow），已上傳檔案直接列在分類行下方。
 *
 * 與精靈的差異：詳情頁的檔案是已落庫的 case_documents，
 * 上傳即時 POST /api/cases/:id/documents 落庫（精靈是先暫存再批量掛載）。
 *
 * 互鎖規則：
 * - 僅派遣管理者（admin/manager）可上傳 / 移除。
 * - 申請文件審核已通過（isApproved）後鎖定，隱藏上傳按鈕與移除按鈕，避免改動已審文件。
 * - 單份分類（multiple=false）上傳新檔時，先 POST 新檔再移除舊關聯（替換語意）。
 */
function DocumentsTab({ caseDetail }: { caseDetail: CaseDetail }) {
  const qc = useQueryClient();
  const actor = useAuthStore((s) => s.user);
  const canDispatch = !!actor && userCan(actor, 'case.dispatch');

  /** 審核已通過時鎖定上傳/移除 */
  const isApproved = caseDetail.latestDocumentReview?.status === 'approved';
  /** 唯讀視角：非管理者或審核已通過時，隱藏所有寫操作入口 */
  const readOnly = !canDispatch || isApproved;

  /** 目前正在上傳的分類 key（用於該分類上傳按鈕的 loading 態） */
  const [uploadingKey, setUploadingKey] = useState<string | null>(null);
  /** 歷史附件沿用彈窗與勾選狀態。 */
  const [reuseOpen, setReuseOpen] = useState(false);
  const [selectedReusableIds, setSelectedReusableIds] = useState<number[]>([]);

  interface ReusableDocument {
    id: number;
    sourceCaseId: number;
    sourceCaseCode: string;
    docType: string;
    fileId: number;
    filename: string;
    sizeBytes: number;
    mimeType: string;
    note: string | null;
    createdAt: string;
  }

  const reusableQuery = useQuery<{ items: ReusableDocument[] }>({
    queryKey: ['case-reusable-documents', caseDetail.id],
    queryFn: () => apiClient.get(`/api/cases/${caseDetail.id}/reusable-documents`),
    enabled: reuseOpen && canDispatch,
  });

  const reuseMutation = useMutation({
    mutationFn: (sourceDocumentIds: number[]) =>
      apiClient.post(`/api/cases/${caseDetail.id}/documents/reuse`, { sourceDocumentIds }),
    onSuccess: () => {
      message.success('已沿用勾選的歷年文件');
      setReuseOpen(false);
      setSelectedReusableIds([]);
      void qc.invalidateQueries({ queryKey: ['case-detail', caseDetail.id] });
      void qc.invalidateQueries({ queryKey: ['case-reusable-documents', caseDetail.id] });
    },
    onError: (error: Error) => {
      console.error('[dispatch/$caseId] reuse historical documents failed', error);
      message.error(error.message ?? '沿用歷年文件失敗');
    },
  });

  /**
   * 序129 內聯化：未上傳原因（{ 分類標籤: 原因 }），狀態上提至此統一持有——
   * 各分類行（CaseDocCategoryRow）於未上傳時內聯輸入，提交面板（CaseDocumentReviewPanel）
   * 讀取校驗＋序列化提交；提交成功後由面板回呼清空。
   */
  const [perReason, setPerReason] = useState<Record<string, string>>({});

  /** 移除文件關聯 Mutation */
  const removeDocMutation = useMutation({
    mutationFn: (docId: number) =>
      apiClient.delete(`/api/cases/${caseDetail.id}/documents/${docId}`),
    onSuccess: () => {
      message.success('文件關聯已移除');
      void qc.invalidateQueries({ queryKey: ['case-detail', caseDetail.id] });
    },
    onError: (err: Error) => {
      // 業務必要：移除失敗需輸出錯誤日誌
      console.error('[dispatch/$caseId] remove document failed', err);
      // 透出後端真實錯誤文案（如「申請文件審核已通過，無法再移除文件」）
      message.error(err.message ?? '移除失敗');
    },
  });

  /**
   * 處理單一分類的檔案批量上傳（支持多選與 zip 壓縮包）：
   * 1. expandZipFiles 展開 zip（跳過目錄 / __MACOSX / 隱藏檔；非 zip 原樣通過）
   * 2. uploadFiles 併發直傳 OSS（單檔失敗不中斷其餘）+ 批量 confirm 落庫
   * 3. 逐一 POST /api/cases/:id/documents 建立關聯（docType = 分類 key）
   * 4. 單份分類：僅取展開後第一個檔案，掛上後移除舊關聯（替換語意）
   * 5. 彙總成功/失敗提示，invalidate case-detail 刷新列表
   */
  const handleCategoryUpload = async (rawFiles: File[], category: CaseDocCategory) => {
    setUploadingKey(category.key);
    // 用固定 key 的 message 呈現批量進度（loading → success/warning 原地更新）
    const msgKey = `case-doc-upload-${category.key}`;
    try {
      // ── 1) zip 展開 ─────────────────────────────────────────────────────────
      let files: File[];
      try {
        files = await expandZipFiles(rawFiles);
      } catch (err) {
        // 業務必要：解壓失敗需輸出錯誤日誌（expandZipFiles 內已記錄細節，這裡記上下文）
        console.error('[dispatch/$caseId] expand zip failed', {
          docType: category.key,
          filenames: rawFiles.map((f) => f.name),
          err,
        });
        message.error(err instanceof Error ? err.message : '解壓縮失敗');
        return;
      }

      // 單份分類只取第一個檔案（多選/壓縮包多檔時提示用戶）
      if (!category.multiple && files.length > 1) {
        message.warning(`「${category.label}」僅允許單份，已取第一個檔案`);
        files = files.slice(0, 1);
      }
      if (files.length === 0) {
        message.warning('沒有可上傳的檔案（壓縮包可能為空）');
        return;
      }

      // ── 2) 單份分類：記錄需替換的舊關聯 ────────────────────────────────────
      const stale = category.multiple
        ? []
        : caseDetail.documents.filter((d) => d.docType === category.key);

      // ── 3) 批量直傳 OSS（併發池 3，批量 confirm 落庫） ─────────────────────
      message.open({
        key: msgKey,
        type: 'loading',
        content: `「${category.label}」上傳中 0/${files.length}…`,
        duration: 0,
      });
      const result = await uploadFiles(files, {
        scope: 'case',
        ownerType: 'case',
        ownerId: caseDetail.id,
        onProgress: (done, total, current) => {
          message.open({
            key: msgKey,
            type: 'loading',
            content: `「${category.label}」上傳中 ${done}/${total}：${current}`,
            duration: 0,
          });
        },
      });

      // ── 4) 掛載 case_documents 關聯（小併發池，避免瞬間打爆瀏覽器連接數） ──
      const attachFailed: { filename: string; error: unknown }[] = [];
      await runWithConcurrency(result.succeeded, 3, async (uploaded) => {
        try {
          await apiClient.post(`/api/cases/${caseDetail.id}/documents`, {
            fileId: uploaded.id,
            docType: category.key,
          });
        } catch (err) {
          // 業務必要：掛載失敗需輸出錯誤日誌（文件已落庫，但未關聯到案件）
          console.error('[dispatch/$caseId] attach document failed', {
            caseId: caseDetail.id,
            fileId: uploaded.id,
            docType: category.key,
            err,
          });
          attachFailed.push({ filename: uploaded.filename, error: err });
        }
      });
      const attachedCount = result.succeeded.length - attachFailed.length;

      // ── 5) 替換語意：新檔已成功掛上時才移除舊關聯 ──────────────────────────
      if (stale.length > 0 && attachedCount > 0) {
        // 移除失敗僅記錄，不阻塞新檔已成功落庫的結果
        const results = await Promise.allSettled(
          stale.map((d) => apiClient.delete(`/api/cases/${caseDetail.id}/documents/${d.id}`)),
        );
        const failedRemovals = results.filter((r) => r.status === 'rejected');
        if (failedRemovals.length > 0) {
          // 業務必要：舊關聯移除失敗需輸出錯誤日誌
          console.error('[dispatch/$caseId] replace stale documents failed', {
            caseId: caseDetail.id,
            docType: category.key,
            failures: failedRemovals.map((r) => r.reason),
          });
        }
      }

      // ── 彙總提示 ────────────────────────────────────────────────────────────
      const failedNames = [
        ...result.failed.map((f) => f.file.name),
        ...attachFailed.map((f) => f.filename),
      ];
      if (failedNames.length === 0) {
        message.open({
          key: msgKey,
          type: 'success',
          content: `「${category.label}」已上傳 ${attachedCount} 個檔案`,
        });
      } else {
        // 業務必要：批量上傳部分失敗需輸出錯誤日誌（含失敗檔名清單）
        console.error('[dispatch/$caseId] batch upload partially failed', {
          caseId: caseDetail.id,
          docType: category.key,
          succeeded: attachedCount,
          failed: failedNames,
        });
        const preview = failedNames.slice(0, 3).join('、');
        message.open({
          key: msgKey,
          type: 'warning',
          content:
            `「${category.label}」成功 ${attachedCount} 個、失敗 ${failedNames.length} 個` +
            `（${preview}${failedNames.length > 3 ? ' 等' : ''}），請重試失敗檔案`,
          duration: 6,
        });
      }
      void qc.invalidateQueries({ queryKey: ['case-detail', caseDetail.id] });
    } catch (err) {
      // 業務必要：批量流程意外失敗需輸出錯誤日誌，便於排查 OSS / network 問題
      console.error('[dispatch/$caseId] document batch upload failed', {
        docType: category.key,
        count: rawFiles.length,
        err,
      });
      message.open({ key: msgKey, type: 'error', content: '批量上傳失敗，請重試' });
    } finally {
      setUploadingKey(null);
    }
  };

  /** 渲染某分類下的已上傳檔案列表（單行檔名連結 + 移除按鈕） */
  const renderDocList = (docs: CaseDocument[]) => {
    if (docs.length === 0) return null;
    return (
      <List<CaseDocument>
        size="small"
        style={{ marginTop: 8 }}
        dataSource={docs}
        renderItem={(doc) => (
          <List.Item
            actions={
              readOnly
                ? []
                : [
                    <Popconfirm
                      key="remove"
                      title="確認移除該文件關聯？"
                      description="僅移除案件與文件的關聯關係，文件實體本身不會被刪除。"
                      okText="移除"
                      okType="danger"
                      cancelText="取消"
                      onConfirm={() => removeDocMutation.mutate(doc.id)}
                    >
                      <Button
                        type="text"
                        danger
                        size="small"
                        icon={<X size={14} />}
                        loading={removeDocMutation.isPending}
                      />
                    </Popconfirm>,
                  ]
            }
          >
            {doc.filename ? (
              <Space>
                <FileLink
                  fileId={doc.fileId}
                  filename={doc.filename}
                  sizeBytes={doc.sizeBytes ?? undefined}
                />
                {doc.sourceCaseId && <Tag color="blue">沿用自案件 #{doc.sourceCaseId}</Tag>}
              </Space>
            ) : (
              // 文件被軟刪除時 LEFT JOIN 拿不到 filename，退化成只顯示 ID 以便排查
              <Typography.Text type="secondary">#{doc.fileId}（已刪除）</Typography.Text>
            )}
          </List.Item>
        )}
      />
    );
  };

  // 不屬於 11 類 SOP 申請材料的文件（如手動關聯的「報價單」「繳費單」或歷史標籤），
  // 統一歸入「其他關聯文件」區塊呈現，確保不遺漏任何已掛載文件。
  const knownKeys = new Set(CASE_DOC_CATEGORIES.map((c) => c.key));
  const uncategorized = caseDetail.documents.filter((d) => !knownKeys.has(d.docType));

  return (
    <div>
      {/* 序110：申請表線上填寫連結（生成/複製連結＋填寫狀態＋查看內容） */}
      {actor && userCan(actor, 'case.dispatch') && (
        <OnlineApplicationCard caseDetail={caseDetail} />
      )}

      {/* 應附文件提交面板（序57 新版：提交即生效並自動生成合約評審；下游報價/繳費守衛靠這條流水）。
          missingCategories＝11 類 SOP 分類中尚未上傳的分類標籤，有缺漏時提交須填原因說明。 */}
      {actor && (
        <div style={{ marginBottom: 16 }}>
          <CaseDocumentReviewPanel
            caseId={caseDetail.id}
            caseCode={caseDetail.caseCode}
            actor={{ id: actor.id, roles: actor.roles, capabilities: actor.capabilities }}
            currentDocumentIds={caseDetail.documents.map((d) => d.id)}
            missingCategories={CASE_DOC_CATEGORIES.filter(
              (cat) => !caseDetail.documents.some((d) => d.docType === cat.key),
            ).map((cat) => cat.label)}
            perReason={perReason}
            onPerReasonChange={setPerReason}
          />
        </div>
      )}

      <div className="mb-4 flex items-center justify-between">
        <Typography.Text strong>應附文件 ({caseDetail.documents.length})</Typography.Text>
        {!readOnly && (
          <Button
            icon={<Copy size={14} />}
            onClick={() => {
              setSelectedReusableIds([]);
              setReuseOpen(true);
            }}
          >
            沿用歷年文件
          </Button>
        )}
      </div>

      {!readOnly && (
        <Alert
          type="info"
          message="依照 SOP 申請材料分類，逐項上傳對應檔案；標示「可多份」的分類允許持續加入，且支持一次多選與 zip 壓縮包（自動解壓逐個上傳）。"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}
      {isApproved && (
        <Alert
          type="success"
          message="應附文件已提交，文件清單已鎖定，不可再上傳或移除。"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        {CASE_DOC_CATEGORIES.map((cat) => {
          const docs = caseDetail.documents.filter((d) => d.docType === cat.key);
          // 單份分類已有檔案時，上傳按鈕語意改為「替換」
          const replaceMode = !cat.multiple && docs.length > 0;
          return (
            <CaseDocCategoryRow
              key={cat.key}
              category={cat}
              hideUpload={readOnly}
              disabled={uploadingKey !== null}
              uploading={uploadingKey === cat.key}
              replaceMode={replaceMode}
              onFiles={(files) => void handleCategoryUpload(files, cat)}
              // 序129 內聯化：本分類可上傳且尚無已上傳檔案時，於同行內聯顯示未上傳原因輸入框
              showReason={!readOnly && docs.length === 0}
              reasonValue={perReason[cat.label] ?? ''}
              onReasonChange={(v) => setPerReason((prev) => ({ ...prev, [cat.label]: v }))}
            >
              {renderDocList(docs)}
            </CaseDocCategoryRow>
          );
        })}

        {/* 其他（非 SOP 分類）關聯文件 */}
        {uncategorized.length > 0 && (
          <div
            style={{
              border: '1px solid #e2e8f0',
              borderRadius: 8,
              padding: 12,
              background: '#ffffff',
            }}
          >
            <Typography.Text strong>其他關聯文件</Typography.Text>
            {renderDocList(uncategorized)}
          </div>
        )}
      </Space>

      <Modal
        title="沿用同一客戶的歷年文件"
        open={reuseOpen}
        width={900}
        onCancel={() => setReuseOpen(false)}
        okText="沿用勾選文件"
        cancelText="取消"
        okButtonProps={{
          disabled: selectedReusableIds.length === 0,
          loading: reuseMutation.isPending,
        }}
        onOk={() => reuseMutation.mutate(selectedReusableIds)}
      >
        <Alert
          type="info"
          showIcon
          message="仅建立本案文件关联，不会修改历史案件或复制 OSS 文件；后续上传新文件即代表替换本年度资料。"
          style={{ marginBottom: 12 }}
        />
        <Table<ReusableDocument>
          rowKey="id"
          size="small"
          loading={reusableQuery.isLoading}
          dataSource={reusableQuery.data?.items ?? []}
          pagination={{ pageSize: 10 }}
          rowSelection={{
            selectedRowKeys: selectedReusableIds,
            onChange: (keys) => setSelectedReusableIds(keys.map(Number)),
          }}
          columns={[
            { title: '來源案件', dataIndex: 'sourceCaseCode', width: 180 },
            { title: '文件類型', dataIndex: 'docType', width: 180 },
            { title: '檔名', dataIndex: 'filename', ellipsis: true },
            {
              title: '建立日期',
              dataIndex: 'createdAt',
              width: 120,
              render: (value: string) => formatDate(value),
            },
          ]}
          locale={{ emptyText: '沒有可沿用的歷年文件' }}
        />
      </Modal>
    </div>
  );
}

/**
 * 派遣團隊 Tab。
 *
 * 派案 / 改派彈窗欄位：
 * - teamLeadId：稽核組長（必填，單選）
 * - memberIds：稽核組員（選填，多選；自動排除已選為組長者）
 * - reason：派案備注
 * - adminNotes / verificationNotes：管理組 / 驗證組備註（選填，客戶 PPT S23；
 *   落庫案件、案件詳情展示、改派通知帶出）
 *
 * 改派時會用案件目前的團隊預填表單；組長角色固定為「稽核組長」，
 * 其餘成員預設為「稽核員」（更細的角色之後再按需擴充）。
 */
function TeamTab({ caseDetail }: { caseDetail: CaseDetail }) {
  const qc = useQueryClient();
  const [showDispatch, setShowDispatch] = useState(false);
  const [coiError, setCoiError] = useState<string | null>(null);
  const [form] = Form.useForm<{
    teamLeadId?: number;
    memberIds?: number[];
    dueDate?: dayjs.Dayjs;
    /** 稽核完成期限（客戶 PPT S47，選填；落庫 cases.auditDeadline 並隨派案通知帶出） */
    auditDeadline?: dayjs.Dayjs;
    reason?: string;
    /** 管理組備註（派案原因 / 改派說明 / 其他作業，客戶 PPT S23） */
    adminNotes?: string;
    /** 驗證組備註（派案原因 / 改派說明 / 其他作業，客戶 PPT S23） */
    verificationNotes?: string;
  }>();

  // 跟蹤目前選中的組長，用以從組員清單中排除
  const teamLeadId = Form.useWatch('teamLeadId', form);

  const isManager = useAuthStore(
    (s) => s.user!.roles.includes('manager') || s.user!.roles.includes('admin'),
  );

  // 查詢該案件對應的稽核任務（首次派遣後自動建立）。
  // 用於在 TeamTab 顯示「進入稽核任務」連結，串聯派案 → 稽核作業流。
  const { data: auditTasksData } = useQuery<{
    items: { id: number; assigneeId: number; isTeamLead: boolean }[];
  }>({
    queryKey: ['case-audit-tasks', caseDetail.id],
    queryFn: () => apiClient.get(`/api/audit-tasks?caseId=${caseDetail.id}&pageSize=200`),
    // 已繳費 / 稽核中 / 報告中 / 結案 等狀態才有意義
    enabled: caseDetail.workflowState !== '受理建檔' && caseDetail.workflowState !== '已派案',
  });
  // 優先指向組長任務；若無組長任務則指向第一條
  const linkedTaskId = (() => {
    const items = auditTasksData?.items ?? [];
    if (items.length === 0) return null;
    return items.find((t) => t.isTeamLead)?.id ?? items[0]?.id ?? null;
  })();
  const currentUserId = useAuthStore((s) => s.user!.id);

  // 簽名相關：簽名 Modal 開關 + 已確認簽名預覽 Modal 狀態
  const [showSignPad, setShowSignPad] = useState(false);
  const [previewSignatureUrl, setPreviewSignatureUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  /**
   * 提交簽名 Mutation：
   * 1. 收到簽名 PNG File，上傳 OSS 取得 fileId
   * 2. 呼叫 POST /api/cases/:id/team/confirm
   */
  const signMutation = useMutation({
    mutationFn: async (signatureFile: File) => {
      const uploaded = await uploadFile(signatureFile, {
        scope: 'misc',
        ownerType: 'case_audit_team',
        ownerId: caseDetail.id,
      });
      await apiClient.post(`/api/cases/${caseDetail.id}/team/confirm`, {
        signatureFileId: uploaded.id,
      });
    },
    onSuccess: () => {
      message.success('已完成簽名確認');
      setShowSignPad(false);
      void qc.invalidateQueries({ queryKey: ['case-detail', caseDetail.id] });
    },
    onError: (err: { status?: number; message?: string }) => {
      if (err.status === 409) {
        message.error('該成員已確認，無法重簽');
      } else if (err.status === 403) {
        message.error('您不是本案件成員');
      } else {
        message.error(err.message ?? '簽名提交失敗');
      }
    },
  });

  /**
   * 點擊已確認的簽名圖以預覽。
   * 透過 GET /api/files/:id/download 取得 OSS 預簽名 URL，再以圖片顯示。
   */
  const openSignaturePreview = async (signatureFileId: number) => {
    try {
      setPreviewLoading(true);
      const res = await apiClient.get<{ url: string }>(`/api/files/${signatureFileId}/download`);
      setPreviewSignatureUrl(res.url);
    } catch (err) {
      console.error('preview signature failed', err);
      message.error('無法載入簽名圖');
    } finally {
      setPreviewLoading(false);
    }
  };

  // 已存在團隊成員 → 視為「改派」；空團隊 → 首次派案
  const isRedispatch = caseDetail.team.length > 0;
  /**
   * 預派案資格（2026-09-11）：驗證類型非「初次申請」的老客戶案件，
   * 允許在應附文件 / 合約評審 / 報價 / 繳費完成前直接派遣，該四步改為派案後補辦。
   * 判定與後端 dispatchCase 共用 @zhcb/shared 的 isPreDispatchCase。
   */
  const preDispatchEligible = isPreDispatchCase(caseDetail.verificationType);
  /** 本次操作屬於預派案（尚停在前四步的狀態就派遣） */
  const isPreDispatchNow =
    preDispatchEligible &&
    ['受理建檔', '合約已簽', '已報價'].includes(caseDetail.workflowState) &&
    !isRedispatch;
  /**
   * 是否允許指派 / 改派團隊：
   *   - 首次指派：workflowState='已收費'（合約評審通過 → 報價 → 收費之後），
   *               指派組長 + 組員後一步派遣（後端 dispatchCase 自動推進至「已派案」+ 建任務）。
   *   - 預派案：  非初次申請案件在 {受理建檔, 合約已簽, 已報價} 也可直接派遣。
   *   - 改派：    workflowState ∈ {已派案, 稽核中, 報告中, 報告評審, 決定中}。
   * 其他狀態後端會以 422 INVALID_STATE 拒絕。
   */
  const canDispatch =
    ['已收費', '已派案', '稽核中', '報告中', '報告評審', '決定中'].includes(
      caseDetail.workflowState,
    ) || isPreDispatchNow;
  // 首次派遣 / 預派案（尚無團隊 + 管理者）：直接在面板內聯顯示選人表單，不藏在彈窗。
  const showInlineDispatch = isManager && canDispatch && !isRedispatch;

  const { data: auditorsData } = useQuery<{ items: AuditorUser[] }>({
    queryKey: ['auditors'],
    queryFn: () => apiClient.get('/api/settings/staff?pageSize=200&role=auditor'),
    // 內聯首次派遣表單常駐顯示，或改派彈窗開啟時，皆需載入稽核員清單。
    enabled: showDispatch || showInlineDispatch,
  });
  const auditors = (auditorsData?.items ?? []).filter((u) => u.roles?.includes('auditor'));

  /** 共用的搜尋過濾（按 label 子字串匹配） */
  const filterByLabel = (input: string, option?: { label?: unknown }) =>
    ((option?.label as string) ?? '').toLowerCase().includes(input.toLowerCase());

  /** 組員下拉選項（排除已選為組長者） */
  const memberOptions = auditors
    .filter((a) => a.id !== teamLeadId)
    .map((a) => ({ value: a.id, label: `${a.name}（${a.username}）` }));

  /**
   * 開啟派案彈窗。改派時用案件目前的團隊預填：
   * 組長 = team 中 role === '稽核組長' 的第一位（找不到則用 assignedTeamLeadId 對應的成員），
   * 組員 = 其餘所有成員的 userId。
   */
  const openDispatch = () => {
    if (isRedispatch && caseDetail.team.length > 0) {
      const lead =
        caseDetail.team.find((m) => m.role === '稽核組長') ??
        caseDetail.team.find((m) => m.userId === caseDetail.assignedTeamLeadId);
      const leadUserId = lead?.userId;
      const memberIds = caseDetail.team.filter((m) => m.userId !== leadUserId).map((m) => m.userId);
      form.setFieldsValue({
        teamLeadId: leadUserId,
        memberIds,
        // 到期日（SLA 基準）於派遣環節維護：用案件目前的 dueDate 預填
        dueDate: caseDetail.dueDate ? dayjs(caseDetail.dueDate) : undefined,
        // 稽核完成期限（客戶 PPT S47）：用案件既有值預填，改派時可調整
        auditDeadline: caseDetail.auditDeadline ? dayjs(caseDetail.auditDeadline) : undefined,
        reason: undefined,
        // 管理組 / 驗證組備註：用案件既有值預填，改派時可在原文上補寫改派說明
        adminNotes: caseDetail.adminNotes ?? undefined,
        verificationNotes: caseDetail.verificationNotes ?? undefined,
      });
    } else {
      form.resetFields();
    }
    setShowDispatch(true);
  };

  const dispatchMutation = useMutation({
    // PATCH /:id/dispatch 同時服務首次派案與改派，由 service 端依既有團隊判定。
    mutationFn: (v: {
      teamLeadId: number;
      members: Array<{ userId: number; role: string }>;
      dueDate?: string | null;
      auditDeadline?: string | null;
      reason?: string;
      adminNotes?: string;
      verificationNotes?: string;
    }) => apiClient.patch(`/api/cases/${caseDetail.id}/dispatch`, v),
    onSuccess: () => {
      // 首次派遣 / 預派案皆為一步派遣：後端直接推進「已派案」並自動建立稽核任務。
      message.success(
        isRedispatch
          ? '案件已改派'
          : isPreDispatchNow
            ? '已預派案！稽核任務已自動建立；應附文件／合約評審／報價／繳費請於本頁補辦'
            : '已派案！稽核任務已自動建立',
      );
      setShowDispatch(false);
      setCoiError(null);
      form.resetFields();
      void qc.invalidateQueries({ queryKey: ['case-detail', caseDetail.id] });
      void qc.invalidateQueries({ queryKey: ['case-audit-tasks', caseDetail.id] });
    },
    onError: (err: { status?: number; message?: string }) => {
      if (err.status === 409) {
        setCoiError(`利益迴避衝突：${err.message ?? ''}`);
        return;
      }
      // 422 INVALID_STATE 等業務錯誤：直接顯示服務端訊息，避免吃掉狀態提示
      // （理論上 canDispatch 已攔截 422，這裡是兜底）
      message.error(err.message ?? '操作失敗');
    },
  });

  const teamColumns: ColumnsType<TeamMember> = [
    { title: '姓名', dataIndex: 'userName', render: (n: string | null) => n ?? '—' },
    { title: '帳號', dataIndex: 'userUsername', render: (n: string | null) => n ?? '—' },
    { title: '角色', dataIndex: 'role' },
    {
      title: '確認',
      key: 'confirmation',
      width: 120,
      render: (_v: unknown, record: TeamMember) => {
        // 已確認：點擊可預覽簽名圖（不可重簽）
        if (record.confirmedAt) {
          return (
            <span
              role={record.signatureFileId ? 'button' : undefined}
              tabIndex={record.signatureFileId ? 0 : -1}
              className={record.signatureFileId ? 'cursor-pointer' : undefined}
              onClick={
                record.signatureFileId
                  ? () => void openSignaturePreview(record.signatureFileId!)
                  : undefined
              }
              onKeyDown={(e) => {
                if (record.signatureFileId && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  void openSignaturePreview(record.signatureFileId);
                }
              }}
              title={record.signatureFileId ? '點擊查看簽名' : undefined}
            >
              <StatusPill tone="success" size="sm">
                已確認
              </StatusPill>
            </span>
          );
        }
        // 待確認：只有「自己那一行」可點擊，點擊喚起簽名 Modal
        const isSelf = record.userId === currentUserId;
        if (isSelf) {
          return (
            <span
              role="button"
              tabIndex={0}
              className="cursor-pointer"
              onClick={() => setShowSignPad(true)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setShowSignPad(true);
                }
              }}
              title="點擊簽名確認"
            >
              <StatusPill tone="warning" size="sm">
                待簽名
              </StatusPill>
            </span>
          );
        }
        return (
          <StatusPill tone="muted" size="sm">
            待確認
          </StatusPill>
        );
      },
    },
  ];

  // 派遣選人表單（首次派遣內聯顯示 / 改派彈窗中顯示，二者互斥共用同一個 form 實例）。
  const dispatchFormBlock = (
    <>
      {coiError && <Alert type="error" message={coiError} className="mb-4" />}
      <Form
        form={form}
        layout="vertical"
        // 首次派遣（內聯表單）也以案件目前 dueDate / auditDeadline 預填；
        // 序105：無既有值時自動導入——證書到期日←客戶最近證書效期；預計完成期限←上次發證日+1年
        initialValues={{
          dueDate: caseDetail.dueDate
            ? dayjs(caseDetail.dueDate)
            : caseDetail.clientLatestCert?.expiresAt
              ? dayjs(caseDetail.clientLatestCert.expiresAt)
              : undefined,
          auditDeadline: caseDetail.auditDeadline
            ? dayjs(caseDetail.auditDeadline)
            : caseDetail.clientLatestCert?.issuedAt
              ? dayjs(caseDetail.clientLatestCert.issuedAt).add(1, 'year')
              : undefined,
        }}
        onFinish={(v) => {
          setCoiError(null);
          if (!v.teamLeadId) return;
          // 過濾掉與組長重複的組員 ID（保險機制）
          const memberIds = (v.memberIds ?? []).filter((id) => id !== v.teamLeadId);
          dispatchMutation.mutate({
            teamLeadId: v.teamLeadId,
            members: [
              { userId: v.teamLeadId, role: '稽核組長' },
              ...memberIds.map((id) => ({ userId: id, role: '稽核員' })),
            ],
            // 到期日（SLA 基準）於派遣環節設定，傳 ISO 字串給後端寫入案件 dueDate；
            // 序105：清空選擇時顯式傳 null 讓後端刪除既有日期
            dueDate: v.dueDate ? v.dueDate.toISOString() : null,
            // 稽核完成期限（客戶 PPT S47）：取所選日期的整天結束（「日前」語義含當日）
            auditDeadline: v.auditDeadline ? v.auditDeadline.endOf('day').toISOString() : null,
            reason: v.reason,
            // 管理組 / 驗證組備註（客戶 PPT S23）：落庫案件並隨改派通知帶出
            adminNotes: v.adminNotes,
            verificationNotes: v.verificationNotes,
          });
        }}
        // 切換組長後，把已選但與新組長重複的組員自動移除
        onValuesChange={(changed: { teamLeadId?: number }) => {
          if (changed.teamLeadId === undefined) return;
          const current = (form.getFieldValue('memberIds') as number[] | undefined) ?? [];
          const next = current.filter((id) => id !== changed.teamLeadId);
          if (next.length !== current.length) {
            form.setFieldValue('memberIds', next);
          }
        }}
      >
        <Form.Item name="teamLeadId" label="稽核組長" rules={[{ required: true }]}>
          <Select
            showSearch
            placeholder="搜尋並選擇稽核組長…"
            filterOption={filterByLabel}
            options={auditors.map((a) => ({ value: a.id, label: `${a.name}（${a.username}）` }))}
          />
        </Form.Item>
        <Form.Item
          name="memberIds"
          label="稽核組員（選填，可多選）"
          tooltip="預設角色為「稽核員」；如需指定觀察 / 見習 / 驗證助理等角色，可派案後再行調整。"
        >
          <Select
            mode="multiple"
            allowClear
            showSearch
            placeholder={teamLeadId ? '搜尋並選擇組員…' : '請先選擇組長'}
            disabled={!teamLeadId}
            filterOption={filterByLabel}
            options={memberOptions}
            maxTagCount="responsive"
          />
        </Form.Item>
        {/* 序105：預計到期日改名「證書到期日」，自客戶最近證書效期自動帶入；可清除 */}
        <Form.Item
          name="dueDate"
          label="證書到期日"
          tooltip="自客戶上一年度證書有效期自動提取（無證書的新申請案件可手動選擇）；作為 SLA 逾期預警基準，可清除。"
        >
          <DatePicker
            style={{ width: '100%' }}
            format="YYYY/MM/DD"
            placeholder="請選擇日期"
            allowClear
          />
        </Form.Item>
        {/* 稽核完成期限（客戶 PPT S47）：派案時指定「稽核完成時間：XXXX年XX月XX日前」，
            落庫案件並隨派案通知（case.dispatch.assigned）帶出期限文案。
            序105：預設導入上次發證日+1年（新申請案件手動錄入）；可清除。 */}
        <Form.Item
          name="auditDeadline"
          label="預計完成期限"
          tooltip="系統預設導入上次發證日＋1 年（新申請案件請手動選擇）；指定稽核作業須於「XXXX年XX月XX日前」完成，隨派案通知帶出；可清除。"
        >
          <DatePicker
            style={{ width: '100%' }}
            format="YYYY/MM/DD"
            placeholder="請選擇日期"
            allowClear
          />
        </Form.Item>
        <Form.Item name="reason" label="備注">
          <Input.TextArea rows={2} maxLength={500} />
        </Form.Item>
        {/* 管理組 / 驗證組備註（客戶 PPT S23）：記錄派案原因 / 改派說明 / 其他作業事項，
            落庫於案件並於案件詳情展示；改派時隨 case.dispatch.changed 通知帶出。 */}
        <Form.Item
          name="adminNotes"
          label="管理組備註"
          tooltip="記錄派案原因、改派說明或其他作業事項；將顯示於案件詳情並隨改派通知帶出"
        >
          <Input.TextArea rows={2} maxLength={1000} showCount placeholder="管理組備註（選填）" />
        </Form.Item>
        <Form.Item
          name="verificationNotes"
          label="驗證組備註"
          tooltip="記錄派案原因、改派說明或其他作業事項；將顯示於案件詳情並隨改派通知帶出"
        >
          <Input.TextArea rows={2} maxLength={1000} showCount placeholder="驗證組備註（選填）" />
        </Form.Item>
        <Button type="primary" htmlType="submit" loading={dispatchMutation.isPending}>
          {isRedispatch ? '確認改派' : isPreDispatchNow ? '確定預派案' : '確定派遣'}
        </Button>
      </Form>
    </>
  );

  return (
    <div>
      {/* 案件已進入稽核作業流：提供「進入稽核任務」捷徑（繳費確認後自動建立任務） */}
      {linkedTaskId && (
        <Alert
          type="info"
          showIcon
          message={
            <Space wrap>
              <span>本案件已產生稽核任務</span>
              <a href={`/audit-tasks/${linkedTaskId}`}>進入稽核任務 #{linkedTaskId} →</a>
              <TaskPackButton taskId={linkedTaskId} size="small" />
            </Space>
          }
          style={{ marginBottom: 16 }}
        />
      )}
      <div className="mb-4 flex justify-between">
        <Typography.Text strong>稽核團隊 ({caseDetail.team.length})</Typography.Text>
        {/* 改派入口僅在「已有團隊 + 可派遣」時提供（彈窗）；首次派遣走下方內聯表單。 */}
        {isManager && isRedispatch && canDispatch && (
          <Button icon={<RefreshCw size={14} />} size="small" onClick={openDispatch}>
            改派
          </Button>
        )}
      </div>

      {isRedispatch ? (
        // 已有團隊：展示成員表
        <Table<TeamMember>
          dataSource={caseDetail.team}
          rowKey="id"
          size="small"
          columns={teamColumns}
        />
      ) : showInlineDispatch ? (
        // 首次派遣（已收費）/ 預派案（非初次申請）：內聯選人表單，選好組長/組員點
        // 「確定派遣」即一步派遣 → 已派案 + 建任務
        <div>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message={
              isPreDispatchNow
                ? `驗證類型「${caseDetail.verificationType}」非初次申請，可直接預派案`
                : '案件已完成繳費，請選擇稽核組長與組員後派遣'
            }
            description={
              isPreDispatchNow
                ? '點擊「確定預派案」後，案件直接進入「已派案」並自動建立稽核任務；應附文件、合約評審、報價、繳費四步已解鎖，可於派案後在本頁對應步驟補辦。'
                : '點擊「確定派遣」後，案件直接進入「已派案」並自動建立稽核任務，組員可在「我的任務」中看到。'
            }
          />
          {dispatchFormBlock}
        </div>
      ) : isManager ? (
        // 尚未到「已收費」且不適用預派案（含初次申請）：派遣未解鎖，明確告知還差哪些步驟
        <Alert
          type="warning"
          showIcon
          message="尚不可派遣團隊"
          description={
            caseDetail.workflowState === '合約已簽'
              ? '請先在「報價」步驟完成報價、再到「繳費」步驟登記繳款；案件進入「已收費」後即可在此選擇稽核組長與組員派遣。'
              : caseDetail.workflowState === '已報價'
                ? '請先到「繳費」步驟登記繳款；案件進入「已收費」後即可在此選擇稽核組長與組員派遣。'
                : `案件目前在「${caseDetail.workflowState}」階段，尚不可派遣。`
          }
        />
      ) : (
        <Typography.Text type="secondary">尚未派案</Typography.Text>
      )}

      {/* 改派彈窗（僅改派場景使用；首次派遣已改為內聯） */}
      <Modal
        title="改派稽核團隊"
        open={showDispatch}
        onCancel={() => {
          setShowDispatch(false);
          setCoiError(null);
          form.resetFields();
        }}
        footer={null}
      >
        {dispatchFormBlock}
      </Modal>

      {/* 簽名確認 Modal（自己那一行的「待簽名」按鈕觸發） */}
      <SignaturePadModal
        open={showSignPad}
        title="派案確認簽名"
        onCancel={() => setShowSignPad(false)}
        loading={signMutation.isPending}
        onSubmit={async (file) => {
          await signMutation.mutateAsync(file);
        }}
      />

      {/* 已確認的簽名圖預覽 Modal */}
      <Modal
        open={previewSignatureUrl !== null || previewLoading}
        title="簽名"
        footer={null}
        onCancel={() => setPreviewSignatureUrl(null)}
        width={640}
      >
        {previewLoading && !previewSignatureUrl ? (
          <div className="py-8 text-center text-gray-500">載入中…</div>
        ) : previewSignatureUrl ? (
          <img
            src={previewSignatureUrl}
            alt="簽名"
            className="block w-full bg-white border border-gray-200 rounded"
          />
        ) : null}
      </Modal>
    </div>
  );
}

/** 報價 Tab */
function QuotationsTab({ caseDetail }: { caseDetail: CaseDetail }) {
  const qc = useQueryClient();
  /** 當前登入用戶（用於前端權限 gate） */
  const user = useAuthStore((s) => s.user);
  /** 報價操作使用動態能力判斷，會計與自定義報價角色都可正常顯示入口。 */
  const canManageQuotations = !!user && userCan(user, 'case.quotation.manage');
  /** 僅 admin 可就地編輯已建立的報價（糾錯入口） */
  const isAdmin = user?.roles.includes('admin');
  const [showModal, setShowModal] = useState(false);
  /** 正在編輯的報價單；null 表示「新增」模式 */
  const [editing, setEditing] = useState<Quotation | null>(null);
  const [form] = Form.useForm();
  /** 已上傳的報價單附件（提交時帶入 fileId） */
  const [attachment, setAttachment] = useState<UploadedFile | null>(null);
  /** 上傳進行中標記 */
  const [uploading, setUploading] = useState(false);

  /** 統一關閉 Modal 並清理表單 / 附件 / 編輯態 */
  const closeModal = () => {
    setShowModal(false);
    setEditing(null);
    form.resetFields();
    setAttachment(null);
  };

  /** 打開「新增」Modal */
  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    setAttachment(null);
    setShowModal(true);
  };

  /** 打開「編輯」Modal 並以該行資料預填（金額由分→元） */
  const openEdit = (record: Quotation) => {
    setEditing(record);
    form.setFieldsValue({
      amount: record.amount / 100,
      notes: record.notes ?? undefined,
      paymentDueAt: record.paymentDueAt ? dayjs(record.paymentDueAt) : undefined,
    });
    setAttachment(
      record.fileId && record.attachmentFilename
        ? ({
            id: record.fileId,
            filename: record.attachmentFilename,
            ossKey: '',
            sizeBytes: 0,
            mimeType: '',
          } satisfies UploadedFile)
        : null,
    );
    setShowModal(true);
  };

  /**
   * 完成報價 mutation：呼叫 finalize endpoint，將 workflowState 由「合約已簽」推進至「已報價」。
   * 成功後重新拉取 case-detail，刷新進度條與按鈕可見性。
   */
  const finalizeMutation = useMutation({
    mutationFn: () => apiClient.post(`/api/cases/${caseDetail.id}/quotations/finalize`, {}),
    onSuccess: () => {
      message.success('報價已完結，案件進入「已報價」狀態');
      void qc.invalidateQueries({ queryKey: ['case-detail', caseDetail.id] });
    },
    onError: (err: Error) => {
      console.error('[QuotationsTab] finalize failed', err);
      message.error(err.message ?? '完結報價失敗');
    },
  });

  const createMutation = useMutation({
    mutationFn: (v: {
      amount: number;
      notes?: string;
      fileId?: number;
      paymentDueAt?: dayjs.Dayjs | null;
    }) =>
      apiClient.post(`/api/cases/${caseDetail.id}/quotations`, {
        ...v,
        amount: Math.round(v.amount * 100), // 元 → 分
        // 繳費截止日期（序103）：Dayjs → ISO 字串
        paymentDueAt: v.paymentDueAt ? v.paymentDueAt.toISOString() : undefined,
      }),
    onSuccess: () => {
      message.success('報價單已建立');
      closeModal();
      void qc.invalidateQueries({ queryKey: ['case-detail', caseDetail.id] });
    },
    // 透出後端真實錯誤文案（如「申請文件審核」gate 的可操作提示），避免只顯示泛用「操作失敗」
    onError: (err: Error) => {
      console.error('[QuotationsTab] create quotation failed', err);
      message.error(err.message ?? '建立報價失敗');
    },
  });

  /**
   * 編輯報價 mutation（admin 糾錯）：PATCH 就地更新字段，不影響案件狀態。
   */
  const updateMutation = useMutation({
    mutationFn: (v: {
      id: number;
      amount: number;
      notes?: string;
      fileId?: number;
      paymentDueAt?: dayjs.Dayjs | null;
    }) =>
      apiClient.patch(`/api/cases/${caseDetail.id}/quotations/${v.id}`, {
        amount: Math.round(v.amount * 100), // 元 → 分
        notes: v.notes,
        fileId: v.fileId,
        // 繳費截止日期（序103）：清空傳 null，未動傳 undefined 保持原值
        paymentDueAt: v.paymentDueAt ? v.paymentDueAt.toISOString() : null,
      }),
    onSuccess: () => {
      message.success('報價單已更新');
      closeModal();
      void qc.invalidateQueries({ queryKey: ['case-detail', caseDetail.id] });
    },
    onError: (err: Error) => {
      console.error('[QuotationsTab] update quotation failed', err);
      message.error(err.message ?? '更新報價失敗');
    },
  });

  /** 直傳 OSS 並把 fileId 暫存到本地 state */
  const handleUpload = async (file: File) => {
    setUploading(true);
    try {
      const uploaded = await uploadFile(file, {
        scope: 'case',
        ownerType: 'case',
        ownerId: caseDetail.id,
      });
      setAttachment(uploaded);
      message.success(`報價單已上傳：${uploaded.filename}`);
    } catch (err) {
      console.error('quotation upload failed', err);
      message.error('附件上傳失敗');
    } finally {
      setUploading(false);
    }
  };

  const columns: ColumnsType<Quotation> = [
    {
      title: '編號',
      dataIndex: 'quotationCode',
      width: 160,
      render: (v: string) => <span style={{ fontFamily: 'monospace' }}>{v}</span>,
    },
    {
      title: '金額（元）',
      dataIndex: 'amount',
      width: 130,
      render: (a: number) => `NT$ ${(a / 100).toLocaleString()}`,
    },
    {
      title: '附件',
      key: 'attachment',
      width: 220,
      ellipsis: true,
      render: (_, record) =>
        record.fileId && record.attachmentFilename ? (
          <FileLink fileId={record.fileId} filename={record.attachmentFilename} compact />
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        ),
    },
    { title: '備注', dataIndex: 'notes', render: (n: string | null) => n ?? '—', ellipsis: true },
    {
      // 繳費截止日期（序103）：已逾期且未收款時紅字提示
      title: '繳費截止',
      dataIndex: 'paymentDueAt',
      width: 120,
      render: (d: string | null) =>
        d ? (
          <span style={{ color: dayjs(d).isBefore(dayjs(), 'day') ? '#cf1322' : undefined }}>
            {dayjs(d).format('YYYY-MM-DD')}
          </span>
        ) : (
          '—'
        ),
    },
    {
      title: '發出',
      dataIndex: 'issuedAt',
      width: 120,
      render: (d: string | null) => (d ? formatDate(d) : '—'),
    },
    {
      title: '客戶確認',
      dataIndex: 'confirmedAt',
      width: 100,
      render: (d: string | null) =>
        d ? (
          <StatusPill tone="success" size="sm">
            已確認
          </StatusPill>
        ) : (
          <StatusPill tone="muted" size="sm">
            待確認
          </StatusPill>
        ),
    },
    // 操作列：僅 admin 顯示「編輯」糾錯入口（任何狀態可改，不影響案件流程）
    ...(isAdmin
      ? [
          {
            title: '操作',
            key: 'actions',
            width: 80,
            render: (_: unknown, record: Quotation) => (
              <Button
                type="link"
                size="small"
                icon={<Pencil size={14} />}
                onClick={() => openEdit(record)}
              >
                編輯
              </Button>
            ),
          } as ColumnsType<Quotation>[number],
        ]
      : []),
  ];

  return (
    <div>
      <div className="mb-4 flex justify-between">
        <Typography.Text strong>報價單 ({caseDetail.quotations.length})</Typography.Text>
        <div style={{ display: 'flex', gap: 8 }}>
          {/* 完成報價：具報價能力 + workflowState='合約已簽' + 至少 1 份 quotation 才顯示 */}
          {canFinalizeQuotation({
            canManageQuotations,
            workflowState: caseDetail.workflowState,
            quotationCount: caseDetail.quotations.length,
            // 預派案：報價為派案後補辦，不受「合約已簽」狀態限制
            preDispatched: !!caseDetail.preDispatchedAt,
          }) && (
            <Button
              type="primary"
              size="small"
              icon={<CheckCircle2 size={14} />}
              loading={finalizeMutation.isPending}
              onClick={() =>
                modal.confirm({
                  title: '完成報價？',
                  content: caseDetail.preDispatchedAt
                    ? '本案為預派案，報價完結僅記錄補辦事實，不會改變案件目前的稽核進度。'
                    : '完結後將進入「已報價」狀態，後續可繼續補充或修訂報價，但不影響狀態。',
                  okText: '確認完結',
                  cancelText: '取消',
                  onOk: () => finalizeMutation.mutateAsync(),
                })
              }
            >
              完成報價
            </Button>
          )}
          {/* 新增報價：依動態 case.quotation.manage 能力顯示，包含會計角色。 */}
          {canManageQuotations && (
            <Button icon={<Plus size={14} />} size="small" onClick={openCreate}>
              新增報價
            </Button>
          )}
        </div>
      </div>
      <Table<Quotation>
        dataSource={caseDetail.quotations}
        rowKey="id"
        size="small"
        columns={columns}
      />

      <Modal
        title={editing ? `編輯報價單 ${editing.quotationCode}` : '新增報價單'}
        open={showModal}
        onCancel={closeModal}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(v: { amount: number; notes?: string; paymentDueAt?: dayjs.Dayjs | null }) =>
            editing
              ? updateMutation.mutate({ id: editing.id, ...v, fileId: attachment?.id })
              : createMutation.mutate({ ...v, fileId: attachment?.id })
          }
        >
          <Form.Item name="amount" label="報價金額（元）" rules={[{ required: true }]}>
            <InputNumber min={0} step={1000} addonBefore="NT$" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="paymentDueAt"
            label="繳費截止日期"
            tooltip="告知客戶的繳費期限，將呈現於報價清單列（序103）"
          >
            <DatePicker style={{ width: '100%' }} placeholder="請選擇繳費截止日期" />
          </Form.Item>
          <Form.Item label="報價單附件" extra="支援 PDF / 圖片，建議 PDF">
            {/* 上傳按鈕：已附檔時改為「替換」字樣，避免使用者誤以為要再上傳一份 */}
            <Upload
              accept=".pdf,.jpg,.jpeg,.png"
              showUploadList={false}
              beforeUpload={(file) => {
                void handleUpload(file);
                return false; // 阻止 antd 內建 XHR，改走 OSS 直傳
              }}
            >
              <Button icon={<UploadIcon size={14} />} loading={uploading}>
                {attachment ? '替換報價單' : '上傳報價單'}
              </Button>
            </Upload>
            {/* 已上傳的附件以 FileLink 呈現：可點檔名預覽，✕ 移除 */}
            {attachment && (
              <div style={{ marginTop: 8 }}>
                <FileLink
                  fileId={attachment.id}
                  filename={attachment.filename}
                  sizeBytes={attachment.sizeBytes}
                  onRemove={() => setAttachment(null)}
                />
              </div>
            )}
          </Form.Item>
          <Form.Item name="notes" label="備注">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            loading={createMutation.isPending || updateMutation.isPending}
            disabled={uploading}
          >
            {editing ? '儲存修改' : '建立報價'}
          </Button>
        </Form>
      </Modal>
    </div>
  );
}

/**
 * 繳費類型中文標籤映射（值枚舉同 @zhcb/shared 的 PAYMENT_TYPES）。
 *
 * - full（全款）＝一次繳清，業務效果等同首期款：案件在「已報價」登記時
 *   後端自動推進「已報價→已收費」（客戶 PPT S12 需求）。
 */
const PAYMENT_TYPE_LABELS: Record<string, string> = {
  initial: '首次繳費',
  full: '全款',
  balance: '尾款',
  supplementary: '補繳',
};

/** 繳費 Tab */
function PaymentsTab({ caseDetail }: { caseDetail: CaseDetail }) {
  const qc = useQueryClient();
  /** 當前登入用戶（用於前端權限 gate） */
  const user = useAuthStore((s) => s.user);
  /** 繳費操作使用動態能力判斷，會計與自定義繳費角色都可正常顯示入口。 */
  const canManagePayments = !!user && userCan(user, 'case.payment.manage');
  /** 僅 admin 可就地編輯已登記的繳款（糾錯入口） */
  const isAdmin = user?.roles.includes('admin');
  const [showModal, setShowModal] = useState(false);
  /** 正在編輯的繳款記錄；null 表示「新增」模式 */
  const [editing, setEditing] = useState<Payment | null>(null);
  const [form] = Form.useForm();
  /** 已上傳的繳費憑證（提交時帶入 proofFileId） */
  const [proof, setProof] = useState<UploadedFile | null>(null);
  /** 上傳進行中標記 */
  const [uploading, setUploading] = useState(false);

  /** 統一關閉 Modal 並清理表單 / 憑證 / 編輯態 */
  const closeModal = () => {
    setShowModal(false);
    setEditing(null);
    form.resetFields();
    setProof(null);
  };

  /** 打開「新增」Modal */
  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    setProof(null);
    setShowModal(true);
  };

  /** 打開「編輯」Modal 並以該行資料預填（金額由分→元） */
  const openEdit = (record: Payment) => {
    setEditing(record);
    form.setFieldsValue({
      amount: record.amount / 100,
      paymentType: record.paymentType,
      paymentMethod: record.paymentMethod ?? undefined,
      notes: record.notes ?? undefined,
    });
    setProof(
      record.proofFileId && record.proofFilename
        ? ({
            id: record.proofFileId,
            filename: record.proofFilename,
            ossKey: '',
            sizeBytes: 0,
            mimeType: '',
          } satisfies UploadedFile)
        : null,
    );
    setShowModal(true);
  };

  const createPayment = useMutation({
    mutationFn: (v: {
      amount: number;
      paymentType: string;
      paymentMethod?: string;
      notes?: string;
      proofFileId?: number;
    }) =>
      apiClient.post(`/api/cases/${caseDetail.id}/payments`, {
        ...v,
        amount: Math.round(v.amount * 100), // 元 → 分
      }),
    onSuccess: () => {
      message.success('繳款記錄已登錄');
      closeModal();
      void qc.invalidateQueries({ queryKey: ['case-detail', caseDetail.id] });
    },
    // 透出後端真實錯誤文案（如「申請文件審核」gate 的可操作提示），避免只顯示泛用「操作失敗」
    onError: (err: Error) => {
      console.error('[PaymentsTab] create payment failed', err);
      message.error(err.message ?? '登錄繳款失敗');
    },
  });

  /**
   * 編輯繳款 mutation（admin 糾錯）：PATCH 就地更新字段，不影響案件狀態。
   */
  const updatePaymentMutation = useMutation({
    mutationFn: (v: {
      id: number;
      amount: number;
      paymentType: string;
      paymentMethod?: string;
      notes?: string;
      proofFileId?: number;
    }) =>
      apiClient.patch(`/api/cases/${caseDetail.id}/payments/${v.id}`, {
        amount: Math.round(v.amount * 100), // 元 → 分
        paymentType: v.paymentType,
        paymentMethod: v.paymentMethod,
        notes: v.notes,
        proofFileId: v.proofFileId,
      }),
    onSuccess: () => {
      message.success('繳款記錄已更新');
      closeModal();
      void qc.invalidateQueries({ queryKey: ['case-detail', caseDetail.id] });
    },
    onError: (err: Error) => {
      console.error('[PaymentsTab] update payment failed', err);
      message.error(err.message ?? '更新繳款失敗');
    },
  });

  /** 直傳 OSS 並把 fileId 暫存到本地 state */
  const handleUpload = async (file: File) => {
    setUploading(true);
    try {
      const uploaded = await uploadFile(file, {
        scope: 'case',
        ownerType: 'case',
        ownerId: caseDetail.id,
      });
      setProof(uploaded);
      message.success(`憑證已上傳：${uploaded.filename}`);
    } catch (err) {
      console.error('payment proof upload failed', err);
      message.error('憑證上傳失敗');
    } finally {
      setUploading(false);
    }
  };

  const columns: ColumnsType<Payment> = [
    {
      title: '編號',
      dataIndex: 'paymentCode',
      width: 160,
      render: (v: string) => <span style={{ fontFamily: 'monospace' }}>{v}</span>,
    },
    {
      title: '金額（元）',
      dataIndex: 'amount',
      width: 130,
      render: (a: number) => `NT$ ${(a / 100).toLocaleString()}`,
    },
    {
      title: '類型',
      dataIndex: 'paymentType',
      width: 100,
      // 顯示中文標籤；未知值兜底顯示原始值（防舊資料/髒資料渲染空白）
      render: (t: string) => PAYMENT_TYPE_LABELS[t] ?? t,
    },
    {
      title: '繳費方式',
      dataIndex: 'paymentMethod',
      width: 100,
      render: (m: string | null) => m ?? '—',
    },
    {
      title: '憑證',
      key: 'proof',
      width: 220,
      ellipsis: true,
      render: (_, record) =>
        record.proofFileId && record.proofFilename ? (
          <FileLink fileId={record.proofFileId} filename={record.proofFilename} compact />
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        ),
    },
    {
      title: '繳款日',
      dataIndex: 'paidAt',
      width: 120,
      render: (d: string | null) => (d ? formatDate(d) : '—'),
    },
    {
      title: '狀態',
      dataIndex: 'confirmedAt',
      width: 100,
      // 繳費登記即確認（後端 createPayment 當場寫 confirmedAt），故恆顯示「已確認」；
      // 保留欄位作為記錄展示。舊資料若 confirmedAt 為空仍兜底顯示待確認。
      render: (d: string | null) =>
        d ? (
          <StatusPill tone="success" size="sm">
            已確認
          </StatusPill>
        ) : (
          <StatusPill tone="warning" size="sm" dot>
            待確認
          </StatusPill>
        ),
    },
    // 操作列：僅 admin 顯示「編輯」糾錯入口（任何狀態可改，不影響案件流程）
    ...(isAdmin
      ? [
          {
            title: '操作',
            key: 'actions',
            width: 80,
            render: (_: unknown, record: Payment) => (
              <Button
                type="link"
                size="small"
                icon={<Pencil size={14} />}
                onClick={() => openEdit(record)}
              >
                編輯
              </Button>
            ),
          } as ColumnsType<Payment>[number],
        ]
      : []),
  ];

  return (
    <div>
      <div className="mb-4 flex justify-between">
        <Typography.Text strong>繳費記錄 ({caseDetail.payments.length})</Typography.Text>
        {/* 新增繳費記錄：依動態 case.payment.manage 能力顯示，包含會計角色。 */}
        {canManagePayments && (
          <Button icon={<Plus size={14} />} size="small" onClick={openCreate}>
            新增記錄
          </Button>
        )}
      </div>
      <Table<Payment> dataSource={caseDetail.payments} rowKey="id" size="small" columns={columns} />

      <Modal
        title={editing ? `編輯繳款記錄 ${editing.paymentCode}` : '登錄繳款記錄'}
        open={showModal}
        onCancel={closeModal}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(v: {
            amount: number;
            paymentType: string;
            paymentMethod?: string;
            notes?: string;
          }) =>
            editing
              ? updatePaymentMutation.mutate({ id: editing.id, ...v, proofFileId: proof?.id })
              : createPayment.mutate({ ...v, proofFileId: proof?.id })
          }
        >
          <Form.Item name="amount" label="金額（元）" rules={[{ required: true }]}>
            <InputNumber min={0} step={1000} addonBefore="NT$" style={{ width: '100%' }} />
          </Form.Item>
          {/* 序104：繳費類型預設「全款」 */}
          <Form.Item
            name="paymentType"
            label="繳費類型"
            initialValue="full"
            rules={[{ required: true }]}
          >
            <Select
              options={[
                // 全款＝一次繳清；後端視同首期款自動推進「已報價→已收費」（序104 預設）
                { value: 'full', label: PAYMENT_TYPE_LABELS.full },
                { value: 'initial', label: PAYMENT_TYPE_LABELS.initial },
                { value: 'balance', label: PAYMENT_TYPE_LABELS.balance },
                { value: 'supplementary', label: PAYMENT_TYPE_LABELS.supplementary },
              ]}
            />
          </Form.Item>
          {/* 序104：繳費方式新增「便利商店代收」並作為預設值 */}
          <Form.Item name="paymentMethod" label="繳費方式" initialValue="便利商店代收">
            <Select
              options={['便利商店代收', 'ATM', '電匯', '票據', '現金'].map((m) => ({
                value: m,
                label: m,
              }))}
            />
          </Form.Item>
          <Form.Item label="繳費憑證" extra="支援 PDF / 圖片，請上傳收據或匯款單">
            {/* 上傳按鈕：已附憑證時改為「替換」字樣 */}
            <Upload
              accept=".pdf,.jpg,.jpeg,.png"
              showUploadList={false}
              beforeUpload={(file) => {
                void handleUpload(file);
                return false; // 阻止 antd 內建 XHR，改走 OSS 直傳
              }}
            >
              <Button icon={<UploadIcon size={14} />} loading={uploading}>
                {proof ? '替換憑證' : '上傳繳費憑證'}
              </Button>
            </Upload>
            {/* 已上傳的憑證以 FileLink 呈現：可點檔名預覽，✕ 移除 */}
            {proof && (
              <div style={{ marginTop: 8 }}>
                <FileLink
                  fileId={proof.id}
                  filename={proof.filename}
                  sizeBytes={proof.sizeBytes}
                  onRemove={() => setProof(null)}
                />
              </div>
            )}
          </Form.Item>
          <Form.Item name="notes" label="備注">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            loading={createPayment.isPending || updatePaymentMutation.isPending}
            disabled={uploading}
          >
            {editing ? '儲存修改' : '登錄'}
          </Button>
        </Form>
      </Modal>
    </div>
  );
}

/** 狀態日誌 Tab */
function StateLogTab({ stateLog }: { stateLog: StateLogEntry[] }) {
  return (
    <Timeline
      items={stateLog.map((entry) => ({
        key: entry.id,
        color:
          entry.toState === '已繳費'
            ? 'green'
            : entry.toState === '已派案'
              ? 'blue'
              : entry.toState === 'NC整改中'
                ? 'red'
                : 'gray',
        children: (
          <div>
            <div className="font-medium">
              {entry.fromState ? `${entry.fromState} → ` : ''}
              {entry.toState}
            </div>
            {entry.reason && <div className="text-sm text-gray-500">{entry.reason}</div>}
            <div className="text-xs text-gray-400">
              {formatDateTime(entry.createdAt)}
              {entry.actorName ? ` · 操作人 ${entry.actorName}` : ''}
            </div>
          </div>
        ),
      }))}
    />
  );
}

// ─── 主頁面 ───────────────────────────────────────────────────────────────────

/**
 * 案件詳情頁面主組件。
 */
// ─── 到期日輕量編輯欄位 ───────────────────────────────────────────────────────

/**
 * Header Card 中「到期日」的顯示 + 輕量編輯欄位。
 *
 * 到期日（SLA 基準）正式維護入口在派遣/改派環節，但改派流程較重（需重新提交團隊、
 * 寫改派記錄）；此處提供 admin/manager 一個「只改日期」的快捷入口：
 * 點擊日期旁的編輯圖標 → 行內 DatePicker → 確定後走 PATCH /api/cases/:id 更新 dueDate，
 * 不觸碰團隊與 workflowState。
 */
/**
 * 將稽核完成期限（ISO 字串）格式化為「XXXX年XX月XX日前」文案（客戶 PPT S47）。
 * 未設定期限（null）回 null，由呼叫端渲染為「—」。
 */
function formatAuditDeadlineText(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}年${mm}月${dd}日前`;
}

function DueDateEditable({ caseDetail }: { caseDetail: CaseDetail }) {
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canEdit = isDispatchManager(user?.roles);
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState<dayjs.Dayjs | null>(null);

  const dueDate = caseDetail.dueDate ? new Date(caseDetail.dueDate) : null;
  const isUrgentDue = dueDate && dueDate.getTime() - Date.now() < 7 * 86400000;

  const updateMutation = useMutation({
    // 複用案件基本資料更新端點（updateCaseSchema 含 dueDate，需 case.dispatch 能力）
    mutationFn: (iso: string) => apiClient.patch(`/api/cases/${caseDetail.id}`, { dueDate: iso }),
    onSuccess: () => {
      message.success('到期日已更新');
      setEditing(false);
      void qc.invalidateQueries({ queryKey: ['case-detail', caseDetail.id] });
    },
    onError: (err: { message?: string }) => {
      message.error(err.message ?? '到期日更新失敗');
    },
  });

  if (editing) {
    return (
      <Space size={4}>
        <DatePicker
          size="small"
          format="YYYY/MM/DD"
          value={value}
          onChange={setValue}
          allowClear={false}
          placeholder="請選擇日期"
        />
        <Button
          size="small"
          type="primary"
          loading={updateMutation.isPending}
          disabled={!value}
          onClick={() => value && updateMutation.mutate(value.startOf('day').toISOString())}
        >
          確定
        </Button>
        <Button size="small" disabled={updateMutation.isPending} onClick={() => setEditing(false)}>
          取消
        </Button>
      </Space>
    );
  }

  return (
    <Space size={2}>
      {dueDate ? (
        <span style={{ color: isUrgentDue ? '#e11d48' : undefined }}>
          {formatDate(dueDate)}
          {isUrgentDue && ' ⚠️'}
        </span>
      ) : (
        '—'
      )}
      {canEdit && (
        <Button
          type="text"
          size="small"
          icon={<Pencil size={13} />}
          title="修改到期日"
          onClick={() => {
            setValue(caseDetail.dueDate ? dayjs(caseDetail.dueDate) : null);
            setEditing(true);
          }}
        />
      )}
    </Space>
  );
}

function CaseDetailPage() {
  const navigate = useNavigate();
  const { caseId } = Route.useParams();
  const qc = useQueryClient();
  // 序 55：修改案件權限（系統管理員 admin + 管理組 manager，走 case.dispatch 能力）
  const actorRoles = useAuthStore((s) => s.user?.roles);
  const isManager = isDispatchManager(actorRoles);

  /**
   * 當前聚焦的步驟索引（0–4，對應步驟條與下方內容面板）。
   * null 表示「尚未手動切換」，渲染時回退到由案件狀態推導的預設步驟，
   * 讓使用者初次進入時直接落在當前進行中的階段。
   */
  const [activeStep, setActiveStep] = useState<number | null>(null);
  /** 序 55：修改案件抽屜開關 */
  const [showEdit, setShowEdit] = useState(false);

  /** 序111/112：年度預派案——建立下一年度追蹤/展延案件並沿用團隊直接派遣 */
  const preDispatchMutation = useMutation({
    mutationFn: (verificationType?: string) =>
      apiClient.post<{ case: { id: number; caseCode: string }; planCode: string | null }>(
        `/api/cases/${caseId}/pre-dispatch`,
        verificationType ? { verificationType } : {},
      ),
    onSuccess: (res) => {
      message.success(`已預派下一年度案件 ${res.case.caseCode}（計劃編號 ${res.planCode ?? '—'}）`);
      void navigate({ to: '/dispatch/$caseId', params: { caseId: String(res.case.id) } });
    },
    onError: (err: Error) => {
      console.error('[dispatch/$caseId] pre-dispatch failed', err);
      message.error(err.message || '預派案失敗');
    },
  });

  const { data, isLoading, isError } = useQuery<{ item: CaseDetail }>({
    queryKey: ['case-detail', Number(caseId)],
    queryFn: () => apiClient.get(`/api/cases/${caseId}`),
  });

  const caseDetail = data?.item;

  if (isLoading) {
    return (
      <div className="p-6">
        <Typography.Text>載入中…</Typography.Text>
      </div>
    );
  }

  if (isError || !caseDetail) {
    return (
      <div className="p-6">
        <Alert type="error" message="案件不存在或無法訪問" />
      </div>
    );
  }

  // 當前聚焦步驟：使用者手動點擊後取 activeStep，否則回退到案件進行中的步驟。
  /**
   * 進度條上下文。預派案（preDispatched）時前四步不隨「已派案」一併變綠，
   * 而是按各自真實狀態顯示待補辦，故需額外帶入報價 / 到帳繳費的存在性。
   */
  const stepCtx = {
    workflowState: caseDetail.workflowState,
    contractReviewState: caseDetail.contractReviewState,
    documentReviewStatus: caseDetail.latestDocumentReview?.status ?? null,
    preDispatched: !!caseDetail.preDispatchedAt,
    hasQuotation: caseDetail.quotations.length > 0,
    hasConfirmedPayment: caseDetail.payments.some((p) => !!p.confirmedAt),
    // 非初次申請案件：尚未派案時「派遣」步驟即刻可用（預派案）
    preDispatchEligible: isPreDispatchCase(caseDetail.verificationType),
  };
  const currentStep = activeStep ?? getDefaultActiveStep(stepCtx);

  /** 預派案待補辦步驟名稱（僅預派案案件使用；已完成者不列） */
  const pendingCatchUpSteps = [
    stepCtx.documentReviewStatus === 'approved' ? null : '應附文件',
    caseDetail.contractReviewState === 'accepted' ? null : '合約評審',
    stepCtx.hasQuotation ? null : '報價',
    stepCtx.hasConfirmedPayment ? null : '繳費',
  ].filter((x): x is string => x !== null);

  // 步驟索引 → 內容面板，順序與 CaseProgressSteps 的 STEP_TITLES 嚴格對應：
  // 0 文件審核=文件 / 1 合約評審 / 2 報價 / 3 繳費 / 4 派遣=派遣團隊 / 5 已派案=狀態日誌。
  const STEP_PANELS = [
    <DocumentsTab key="documents" caseDetail={caseDetail} />,
    <ContractReviewPanel
      key="contract"
      caseId={caseDetail.id}
      contractReviewState={caseDetail.contractReviewState}
    />,
    <QuotationsTab key="quotations" caseDetail={caseDetail} />,
    <PaymentsTab key="payments" caseDetail={caseDetail} />,
    <TeamTab key="team" caseDetail={caseDetail} />,
    <StateLogTab key="state-log" stateLog={caseDetail.stateLog} />,
  ];

  return (
    <PageShell
      title={
        <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontFamily: 'monospace' }}>{caseDetail.caseCode}</span>
          <StatusPill tone={STATE_TONE[caseDetail.workflowState] ?? 'neutral'} dot>
            {caseDetail.workflowState}
          </StatusPill>
          {/* 預派案標記：本案先派案、前四步待補辦 */}
          {caseDetail.preDispatchedAt && <Tag color="gold">預派案</Tag>}
        </span>
      }
      description={`${caseDetail.clientName ?? '—'} · ${caseDetail.verificationType}`}
      actions={
        <Space>
          {/* 序111/112：年度預派案（僅管理組、且該客戶已有證書時可用）——
              自動匯入本案資料建立下一年度追蹤/展延案件並沿用稽核團隊直接派遣 */}
          {isManager && caseDetail.clientLatestCert && (
            <Button
              icon={<CalendarPlus size={14} />}
              loading={preDispatchMutation.isPending}
              onClick={() => {
                let vt: string | undefined;
                modal.confirm({
                  title: '年度預派案（下一年度追蹤/展延）',
                  content: (
                    <div style={{ marginTop: 8 }}>
                      <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
                        自動匯入本案件申請資料建立下一年度案件，沿用本案稽核團隊直接派遣
                        （不進文件點檢；合約評審／報價／繳費由管理組另行補辦）。
                        計劃編號沿用本案編號並替換執行年度。
                      </Typography.Paragraph>
                      <Select<string>
                        style={{ width: '100%' }}
                        placeholder="驗證類型（不選則自動遞進：初次→追一→追二→展延）"
                        allowClear
                        options={['追蹤查驗1', '追蹤查驗2', '展延查驗'].map((t) => ({
                          value: t,
                          label: t,
                        }))}
                        onChange={(v) => {
                          vt = v;
                        }}
                      />
                    </div>
                  ),
                  okText: '建立並派遣',
                  cancelText: '取消',
                  onOk: () => preDispatchMutation.mutateAsync(vt),
                });
              }}
            >
              年度預派案
            </Button>
          )}
          {/* 序 55：修改案件（僅系統管理員 / 管理組可見，接 PATCH /api/cases/:id） */}
          {isManager && (
            <Button icon={<Pencil size={14} />} onClick={() => setShowEdit(true)}>
              修改案件
            </Button>
          )}
          <Button onClick={() => void navigate({ to: '/dispatch' })} icon={<ArrowLeft size={14} />}>
            返回列表
          </Button>
        </Space>
      }
    >
      {/* 案件基本資料 */}
      <Card style={{ marginBottom: 32 }} size="small">
        <Row gutter={[16, 0]}>
          <Col xs={24} md={12} lg={8}>
            <Descriptions size="small" column={1}>
              <Descriptions.Item label="客戶">
                {caseDetail.clientCode ? `[${caseDetail.clientCode}] ` : ''}
                {caseDetail.clientName ?? '—'}
              </Descriptions.Item>
              <Descriptions.Item label="驗證方案">{caseDetail.scheme}</Descriptions.Item>
              <Descriptions.Item label="驗證類型">{caseDetail.verificationType}</Descriptions.Item>
              <Descriptions.Item label="驗證資格">
                {/* 多選陣列頓號拼接（後端已回落舊單值資料）；未填顯示 — */}
                {caseDetail.verificationQualifications?.length
                  ? caseDetail.verificationQualifications.join('、')
                  : '—'}
              </Descriptions.Item>
            </Descriptions>
          </Col>
          <Col xs={24} md={12} lg={8}>
            <Descriptions size="small" column={1}>
              <Descriptions.Item label="稽核組長">
                {caseDetail.teamLeadName ?? <Tag color="orange">未派案</Tag>}
              </Descriptions.Item>
              <Descriptions.Item label="派案時間">
                {caseDetail.dispatchedAt
                  ? formatDate(caseDetail.dispatchedAt)
                  : '—'}
                {/* 預派案：標明本次派案走的是預派案（前四步派案後補辦） */}
                {caseDetail.preDispatchedAt && (
                  <Tag color="gold" style={{ marginLeft: 8 }}>
                    預派案
                  </Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="到期日">
                {/* 顯示 + 輕量編輯（僅 admin/manager 可見編輯圖標，不走改派流程） */}
                <DueDateEditable caseDetail={caseDetail} />
              </Descriptions.Item>
              <Descriptions.Item label="稽核完成期限">
                {/* 客戶 PPT S47：派案時指定「稽核完成時間：XXXX年XX月XX日前」，於派遣/改派表單維護 */}
                {formatAuditDeadlineText(caseDetail.auditDeadline) ?? '—'}
              </Descriptions.Item>
            </Descriptions>
          </Col>
        </Row>
        {/* 申請明細（TOP/TAP 分流結構或 LEGACY 舊結構，客戶 PPT S9/S10）：解析失敗時整塊不顯示 */}
        <ApplicationDetailsView details={caseDetail.applicationDetails} />
        {caseDetail.notes && (
          <Typography.Paragraph type="secondary" className="mt-2 mb-0 text-sm">
            備注：{caseDetail.notes}
          </Typography.Paragraph>
        )}
        {/* 管理組 / 驗證組備註（派案 / 改派時填寫，客戶 PPT S23）：有值才顯示 */}
        {caseDetail.adminNotes && (
          <Typography.Paragraph type="secondary" className="mt-2 mb-0 text-sm">
            管理組備註：{caseDetail.adminNotes}
          </Typography.Paragraph>
        )}
        {caseDetail.verificationNotes && (
          <Typography.Paragraph type="secondary" className="mt-2 mb-0 text-sm">
            驗證組備註：{caseDetail.verificationNotes}
          </Typography.Paragraph>
        )}
      </Card>

      {/* 預派案待補辦提示：列出應附文件 / 合約評審 / 報價 / 繳費四步尚未完成者 */}
      {caseDetail.preDispatchedAt && pendingCatchUpSteps.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="本案為預派案，以下步驟尚待補辦"
          description={`${pendingCatchUpSteps.join('、')}；可直接點擊下方步驟條對應步驟辦理，補辦不會影響案件目前的稽核進度。`}
        />
      )}

      {/*
        案件流程進度條，兼任內容切換導航（取代原本底部的 Tabs 及浮於上方的合約評審面板）。
        點擊步驟切換下方面板；activeStep 為 null 時回退到當前進行中的步驟。
        步驟 → 面板映射：文件審核=文件 / 合約評審 / 報價 / 繳費 / 派遣=派遣團隊 / 已派案=狀態日誌。
      */}
      <div style={{ marginBottom: 24 }}>
        <CaseProgressSteps {...stepCtx} activeStep={currentStep} onStepChange={setActiveStep} />
      </div>

      {/* 步驟對應的內容面板（單一面板，由 currentStep 決定） */}
      <Card>{STEP_PANELS[currentStep]}</Card>

      {/* 序 55：修改案件抽屜（僅管理組 / 管理員渲染） */}
      {isManager && (
        <CaseEditDrawer
          open={showEdit}
          onClose={() => setShowEdit(false)}
          initial={{
            id: caseDetail.id,
            clientId: caseDetail.clientId,
            scheme: caseDetail.scheme,
            verificationType: caseDetail.verificationType,
            verificationCategories: caseDetail.verificationCategories,
            contractSource: caseDetail.contractSource,
            applicationDetails: caseDetail.applicationDetails,
            dueDate: caseDetail.dueDate,
            notes: caseDetail.notes,
          }}
          onSaved={() => void qc.invalidateQueries({ queryKey: ['case-detail', caseDetail.id] })}
        />
      )}
    </PageShell>
  );
}

// ─── 序110：申請表線上填寫卡片（生成連結 / 填寫狀態 / 查看內容） ────────────────

/**
 * 管理組於案件詳情生成申請表線上填寫連結交付客戶；
 * 客戶提交後此卡片顯示「已填寫」與提交時間，可展開查看提交內容。
 */
function OnlineApplicationCard({ caseDetail }: { caseDetail: CaseDetail }) {
  const qc = useQueryClient();
  const [viewOpen, setViewOpen] = useState(false);

  const genLink = useMutation({
    mutationFn: () =>
      apiClient.post<{ token: string; path: string }>(
        `/api/cases/${caseDetail.id}/application-link`,
        {},
      ),
    onSuccess: (res) => {
      const url = `${window.location.origin}${res.path}`;
      void navigator.clipboard?.writeText(url).catch(() => {});
      message.success(`填寫連結已生成並複製：${url}`);
      void qc.invalidateQueries({ queryKey: ['case-detail', caseDetail.id] });
    },
    onError: (err: Error) => {
      console.error('[OnlineApplicationCard] generate link failed', err);
      message.error(err.message ?? '生成連結失敗');
    },
  });

  const applyUrl = caseDetail.applicationFormToken
    ? `${window.location.origin}/apply/${caseDetail.applicationFormToken}`
    : null;
  const submission = caseDetail.onlineApplication;

  return (
    <Alert
      style={{ marginBottom: 16 }}
      type={submission ? 'success' : 'info'}
      showIcon
      message={
        <Space wrap>
          <span>申請表線上填寫（序110）：</span>
          {submission ? (
            <>
              <Tag color="success">
                客戶已填寫（{formatDateTime(submission.submittedAt)}）
              </Tag>
              <Button type="link" size="small" onClick={() => setViewOpen(true)}>
                查看內容
              </Button>
            </>
          ) : (
            <Tag>尚未填寫</Tag>
          )}
          {applyUrl ? (
            <Button
              type="link"
              size="small"
              onClick={() => {
                void navigator.clipboard?.writeText(applyUrl).catch(() => {});
                message.success('連結已複製');
              }}
            >
              複製填寫連結
            </Button>
          ) : (
            <Button
              type="link"
              size="small"
              loading={genLink.isPending}
              onClick={() => genLink.mutate()}
            >
              生成填寫連結
            </Button>
          )}
        </Space>
      }
      description={
        viewOpen && submission ? (
          <OnlineApplicationSubmissionView data={submission.data} />
        ) : undefined
      }
    />
  );
}

/** 申請表提交內容的欄位標籤映射（與公開填寫頁 apply.$token.tsx 的表單欄位一一對應） */
const ONLINE_APPLICATION_FIELD_LABELS: Record<string, string> = {
  contactName: '聯絡人姓名',
  contactPhone: '聯絡電話',
  productItems: '產品品項',
  productScopes: '驗證範圍',
  verificationArea: '申請驗證面積（公頃）',
  notes: '其他說明',
};

/**
 * 客戶提交的申請表內容——以表單（Descriptions）形式展示，取代原始 JSON dump。
 * 按公開填寫頁的欄位順序渲染；陣列值以頓號連接；未知欄位兜底顯示原始鍵名。
 */
function OnlineApplicationSubmissionView({ data }: { data: unknown }) {
  if (typeof data !== 'object' || data === null) return null;
  const record = data as Record<string, unknown>;

  /** 單一原始值轉文字：物件類值走 JSON 序列化，避免 [object Object] */
  const stringify = (value: unknown): string =>
    typeof value === 'object' && value !== null ? JSON.stringify(value) : String(value);

  /** 值轉展示文字：陣列以頓號連接，空值顯示「—」 */
  const formatValue = (value: unknown): string => {
    if (value == null || value === '') return '—';
    if (Array.isArray(value)) return value.map(stringify).join('、') || '—';
    return stringify(value);
  };

  // 已知欄位按填寫頁順序排前，未知欄位（防後端擴充）附在後面
  const knownKeys = Object.keys(ONLINE_APPLICATION_FIELD_LABELS).filter((k) => k in record);
  const unknownKeys = Object.keys(record).filter((k) => !(k in ONLINE_APPLICATION_FIELD_LABELS));

  return (
    <Descriptions
      size="small"
      column={2}
      bordered
      style={{ marginTop: 8, background: '#fff', borderRadius: 6 }}
      items={[...knownKeys, ...unknownKeys].map((key) => ({
        key,
        label: ONLINE_APPLICATION_FIELD_LABELS[key] ?? key,
        // 多行文字欄位（其他說明）保留換行
        children: <span style={{ whiteSpace: 'pre-wrap' }}>{formatValue(record[key])}</span>,
        // 長文欄位獨占一行，避免擠壓
        span: key === 'notes' || key === 'productItems' || key === 'productScopes' ? 2 : 1,
      }))}
    />
  );
}
